import { Component } from "react";
import { CommandManager } from "common/app/command-palette/command-manager";
import { Command } from "common/app/command-palette/types";
import { Entity } from "common/entities/types";
import { getExpandedQuery } from "common/query/context";
import { QueryForEntity } from "common/query/types";
// eslint-disable-next-line import/no-cycle
import { ActionsHandler } from "common/record/actions/components/handler";
import { ActionsSelector } from "common/record/actions/components/selector";
import { Context } from "common/types/context";
import { Record } from "common/types/records";
import { GoFn } from "common/types/url";
// eslint-disable-next-line import/no-cycle
import { getActions, hasActions } from "./functions";
import { Action } from "./types";

interface PropTypes {
  context: Context;
  entity: Entity;
  records: Record[];
  reload: (value: any) => any;
  onTheRight: boolean;
  query?: QueryForEntity;
  includeSwitchForm: boolean;
  goTo: GoFn;
  displayType: "label" | "icon";
  label?: string;
  icon?: string;
  predefinedActions?: Action[];
  addToCommandPalette?: boolean;
}

interface StateType {
  action?: Action;
  quickActions?: string[];
}

export class RecordActions extends Component<PropTypes, StateType> {
  static readonly displayName = "RecordActions";
  state: StateType = {};

  private readonly commandManager = new CommandManager();

  componentWillUnmount() {
    this.commandManager.cleanup();
  }

  private readonly hasActionsPropsChanged = (prevProps: PropTypes): boolean => {
    const {
      context,
      entity,
      records,
      query,
      includeSwitchForm,
      predefinedActions,
      addToCommandPalette,
    } = this.props;

    // TODO: This content-based comparison is a workaround for the fact that
    // getRecordsWithActions() in src/x/records/list/functions.ts creates a new
    // array on every render. The proper fix would be to memoize that function
    // so we can use simple reference equality checks here instead.

    if (
      prevProps.context !== context ||
      prevProps.entity !== entity ||
      prevProps.query !== query ||
      prevProps.includeSwitchForm !== includeSwitchForm ||
      prevProps.predefinedActions !== predefinedActions ||
      prevProps.addToCommandPalette !== addToCommandPalette
    ) {
      return true;
    }

    const prevRecords = prevProps.records || [];
    const currentRecords = records || [];

    if (prevRecords.length !== currentRecords.length) {
      return true;
    }

    for (let i = 0; i < prevRecords.length; i++) {
      const prev = prevRecords[i];
      const current = currentRecords[i];

      if (
        prev.properties.id !== current.properties.id ||
        prev.actions.length !== current.actions.length ||
        JSON.stringify(prev.actions) !== JSON.stringify(current.actions)
      ) {
        return true;
      }
    }

    return false;
  };

  addCommandsIfRequested = () => {
    const {
      context,
      entity,
      records = [],
      query,
      includeSwitchForm,
      predefinedActions,
      addToCommandPalette,
    } = this.props;

    if (!addToCommandPalette) {
      this.commandManager.updateCommands([]);
      return;
    }

    const actions = getActions(
      context,
      entity,
      records,
      includeSwitchForm,
      query,
      predefinedActions,
    );

    const commands = actions.reduce((acc, action) => {
      const actions = action[1].map((action): Command => {
        const { name, label, link } = action;
        return {
          id: `${name}${label}`,
          label: label,
          link,
          onClick: !link ? () => this.setAction(action) : undefined,
        };
      });

      return [...acc, ...actions];
    }, []);

    this.commandManager.updateCommands(commands);
  };

  componentDidMount() {
    this.addCommandsIfRequested();
  }

  componentDidUpdate(prevProps: PropTypes) {
    if (this.hasActionsPropsChanged(prevProps)) {
      this.addCommandsIfRequested();
    }
  }

  setAction = (action: Action) => {
    const { records } = this.props;
    if (action?.fn) action.fn?.(records[0].properties);

    this.setState({ action });
  };

  unsetAction = () => this.setState({ action: undefined });

  render() {
    const {
      context,
      entity,
      records = [],
      query,
      displayType,
      goTo,
      reload,
      label,
      icon,
      onTheRight,
      includeSwitchForm,
      predefinedActions,
    } = this.props;
    const { action } = this.state;

    const actions = getActions(
      context,
      entity,
      records,
      includeSwitchForm,
      query,
      predefinedActions,
    );

    if (!hasActions(actions)) return <span />;

    const expandedQuery = query ? getExpandedQuery(query, context) : undefined;

    return (
      <>
        <ActionsSelector
          displayType={displayType}
          icon={icon}
          label={label}
          onTheRight={onTheRight}
          actionGroups={actions}
          onActionSelect={this.setAction}
        />
        {!action?.fn ? (
          <ActionsHandler
            context={context}
            entity={entity}
            records={records}
            query={expandedQuery}
            goTo={goTo}
            reload={reload}
            action={action}
            onActionDismiss={this.unsetAction}
            onActionPerform={this.unsetAction}
          />
        ) : undefined}
      </>
    );
  }
}
